import { Inverter, SystemEnergyState, SystemPowerState , Range, InverterV2, AdvanceEventOutcomes, AdvanceEventOutcomesHistory, EvChRT, EvChRTV2, AHEvChRT, EvChDataV2, AHheatPumpDAY, HeatPumpRTData} from 'ngx-core-services';

import { AlertContent } from '../../lib/alerts/alert.model';
import { blockInfo } from '../../lib/plant-card-overview/plant-card-overview.model';

type BlockStateStatus = 'pending' | 'ready' | 'error';

export interface RealTimeTelemetryUiState {
  active?: boolean;
  duration?: number;
  endedAt?: number;
  lastTelemetryReceivedAt?: number;
  lastTelemetryTs?: number;
  overlayHidden?: boolean;
  overlayRequestedByUser?: boolean;
  startTS?: number | null;
  stopping?: boolean;
  telemetryCount?: number;
}

export interface BlockList {
  SystemTotalizerComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        systemEnergyState?: SystemEnergyState;
        state: BlockStateStatus;
      };
    };
  };
  FlowChartComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        systemPowerState?: SystemPowerState;
        state: BlockStateStatus;
      };
    };
  };
  MonthlyYearlyEnergyOverviewComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        systemEnergyState?: Partial<Inverter>[];
        range: Range;
        mode?: 'month' | 'year';
        state: BlockStateStatus;
      };
    };
  };
  EnergyDataOverviewComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        systemEnergyState?: Partial<Inverter>[];
        range: Range;
        mode?: 'day' | 'month' | 'year';
        state: BlockStateStatus;
      };
    };
  };
  DailyStateOverviewComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        systemEnergyState?: Partial<InverterV2>;
        systemPowerState?: Partial<InverterV2>[];
        rtPowerPoints?: Partial<InverterV2>[];
        range: Range;
        mode?: 'day';
        powerRequestState: BlockStateStatus;
        energyRequestState: BlockStateStatus;
      };
    };
  };
  DailyDevicePowersStateComponent: {
    [InstanceId : string]: {
      [SystemId: string]:
        {
        systemPowerState?: Partial<InverterV2>[];
        range: Range;
        mode?: 'day';
        powerRequestState: BlockStateStatus;
      };
    };
  };
  AlertsListComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        alerts: AlertContent;
        allTimestampsBySerial: { [serial: string]: number[] };
        state: BlockStateStatus;
      };
    };
  };
  AlertsCounterComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        alerts: AlertContent;
        state: BlockStateStatus;
      };
    };
  };
  DeviceDetailsComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        deviceId: string;
        details: Partial<InverterV2>;
        state: BlockStateStatus;
      };
    };
  };
  DeviceListComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        state: BlockStateStatus;
        devices: { [key: string]: Partial<InverterV2> };
      };
    };
  };
  PlantCardComponent: {
    [SystemId: string]: {
      timestamp?: number;
      blocksToShow?: blockInfo[];
      plantStatus?: "connectedAndUpdated" | "disconnected" | "notUpdated" | "updated";
      hasAlarms?: boolean;
      state: BlockStateStatus;
    };
  };
  EventsListComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        eventsHistory: {
          events?: Partial<AdvanceEventOutcomesHistory>[];
          state: BlockStateStatus;
        }
        currentEvent: {
          events?: Partial<AdvanceEventOutcomes>[];
          state: BlockStateStatus;
        };
      };
    };
  };
  EvchargeTelemetryComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        telemetryData?: EvChRT[] | EvChRTV2[] | AHEvChRT[]; // V2: Support for both datalogger types
        range?: Range;
        mode?: 'day' | 'month';
        deviceId?: string;
        dataloggerType?: 'connext' | 'azzurrohub'; // V2: Track datalogger type
        state: BlockStateStatus;
        error?: string;
      };
    };
  };
  EvchargeEnergyAggregationComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        monthlyData?: EvChDataV2[];
        yearlyData?: EvChDataV2[];
        deviceId?: string;
        monthlyState: BlockStateStatus;
        yearlyState: BlockStateStatus;
        error?: string;
      };
    };
  };
  HeatpumpFlowChartComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        [DeviceId: string]: {
          heatpumpData?: Partial<AHheatPumpDAY>;
          state: BlockStateStatus;
        };
      };
    };
  };
  HeatpumpDailyTelemetryComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        [DeviceId: string]: {
          heatpumpData?: Partial<HeatPumpRTData>[];
          state: BlockStateStatus;
        };
      };
    };
  };
  HeatpumpEnergyAggregationComponent: {
    [InstanceId: string]: {
      [SystemId: string]: {
        [DeviceId: string]: {
          monthlyData?: AHheatPumpDAY[];
          yearlyData?: AHheatPumpDAY[];
          monthlyState: BlockStateStatus;
          yearlyState: BlockStateStatus;
          error?: string;
        };
      };
    };
  };
  RealTimeTelemetryComponent: {
    [SystemId: string]: RealTimeTelemetryUiState;
  };
}

export interface BlockState {
  blockList: BlockList;
}

export const initialBlockState: BlockState = {
  blockList: {
    AlertsListComponent: {},
    DailyStateOverviewComponent: {},
    DeviceDetailsComponent: {},
    EnergyDataOverviewComponent: {},
    EvchargeTelemetryComponent: {},
    EvchargeEnergyAggregationComponent: {},
    FlowChartComponent: {},
    MonthlyYearlyEnergyOverviewComponent: {},
    SystemTotalizerComponent: {},
    DeviceListComponent: {},
    AlertsCounterComponent: {},
    DailyDevicePowersStateComponent: {},
    PlantCardComponent: {},
    EventsListComponent: {},
    HeatpumpFlowChartComponent: {},
    HeatpumpDailyTelemetryComponent: {},
    HeatpumpEnergyAggregationComponent: {},
    RealTimeTelemetryComponent: {},
  },
};
